/**
 * HVAC Editor Component
 *
 * Main visual HVAC editor using React Flow for node-graph editing.
 * Handles drag-and-drop, connections, selection, and keyboard shortcuts.
 */

import { useCallback, useRef, useEffect, useMemo, useState } from 'react';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  Panel,
  useReactFlow,
  type OnConnect,
  type OnNodesChange,
  type OnEdgesChange,
  type OnSelectionChangeFunc,
  type NodeTypes,
  applyNodeChanges,
  applyEdgeChanges,
  type ReactFlowInstance,
} from 'reactflow';
import 'reactflow/dist/style.css';

import { useHVACStore } from './hvacStore';
import { HVACNode } from './HVACNode';
import { ComponentPalette } from './ComponentPalette';
import { getComponentDefinition } from './components';
import { useIsMobile } from '../hooks/useMediaQuery';
import type { HVACNodeData, FluidMedium } from './types';

/**
 * Custom node types for React Flow
 */
const nodeTypes: NodeTypes = {
  hvacNode: HVACNode,
};

/**
 * Get minimap node color based on component type
 */
function getMiniMapNodeColor(node: { data?: HVACNodeData }): string {
  if (!node.data?.componentType) return '#E0E0E0';
  const def = getComponentDefinition(node.data.componentType);
  return def?.color || '#E0E0E0';
}

/**
 * Get edge color based on medium
 */
function getEdgeColor(medium?: FluidMedium): string {
  switch (medium) {
    case 'air':
      return '#64B5F6';
    case 'water':
      return '#1976D2';
    case 'refrigerant':
      return '#AB47BC';
    case 'steam':
      return '#BDBDBD';
    default:
      return '#90A4AE';
  }
}

interface HVACEditorProps {
  /** Height of the editor */
  height?: string | number;
}

/**
 * Mobile slide-out drawer for component palette
 */
function MobilePaletteDrawer({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />
      {/* Drawer */}
      <div
        className="absolute left-0 top-0 bottom-0 w-[85vw] max-w-[280px] bg-card shadow-xl flex flex-col"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <h3 className="font-semibold text-foreground">Components</h3>
          <button
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-muted"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          <ComponentPalette />
        </div>
      </div>
    </div>
  );
}

/**
 * HVAC Editor inner component (must be inside ReactFlowProvider)
 */
function HVACEditorInner({ height = '100%' }: HVACEditorProps) {
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const reactFlowInstance = useRef<ReactFlowInstance | null>(null);
  const [showPaletteDrawer, setShowPaletteDrawer] = useState(false);
  const isMobile = useIsMobile();

  // Store state and actions
  const nodes = useHVACStore((state) => state.nodes);
  const edges = useHVACStore((state) => state.edges);
  const setNodes = useHVACStore((state) => state.setNodes);
  const setEdges = useHVACStore((state) => state.setEdges);
  const addNode = useHVACStore((state) => state.addNode);
  const addEdge = useHVACStore((state) => state.addEdge);
  const validateConnection = useHVACStore((state) => state.validateConnection);
  const selectNodes = useHVACStore((state) => state.selectNodes);
  const selectEdges = useHVACStore((state) => state.selectEdges);
  const deleteSelected = useHVACStore((state) => state.deleteSelected);
  const copySelected = useHVACStore((state) => state.copySelected);
  const paste = useHVACStore((state) => state.paste);
  const undo = useHVACStore((state) => state.undo);
  const redo = useHVACStore((state) => state.redo);
  const canUndo = useHVACStore((state) => state.canUndo);
  const canRedo = useHVACStore((state) => state.canRedo);
  const selectedNodeIds = useHVACStore((state) => state.selectedNodeIds);
  const selectedEdgeIds = useHVACStore((state) => state.selectedEdgeIds);
  const isDirty = useHVACStore((state) => state.isDirty);

  const { screenToFlowPosition } = useReactFlow();

  /**
   * Handle node changes (position, selection, removal)
   */
  const onNodesChange: OnNodesChange = useCallback(
    (changes) => {
      setNodes(applyNodeChanges(changes, nodes));
    },
    [nodes, setNodes]
  );

  /**
   * Handle edge changes
   */
  const onEdgesChange: OnEdgesChange = useCallback(
    (changes) => {
      setEdges(applyEdgeChanges(changes, edges));
    },
    [edges, setEdges]
  );

  /**
   * Handle new connection
   */
  const onConnect: OnConnect = useCallback(
    (connection) => {
      addEdge(connection);
    },
    [addEdge]
  );

  /**
   * Validate connection before allowing it
   */
  const isValidConnection = useCallback(
    (connection: { source: string | null; target: string | null; sourceHandle: string | null; targetHandle: string | null }) => {
      const result = validateConnection(connection);
      return result.valid;
    },
    [validateConnection]
  );

  /**
   * Handle selection change
   */
  const onSelectionChange: OnSelectionChangeFunc = useCallback(
    ({ nodes: selectedNodes, edges: selectedEdgesNew }) => {
      selectNodes(selectedNodes.map((n) => n.id));
      selectEdges(selectedEdgesNew.map((e) => e.id));
    },
    [selectNodes, selectEdges]
  );

  /**
   * Handle drag over for drop zone
   */
  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  /**
   * Handle drop to create new node
   */
  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();

      const componentType = event.dataTransfer.getData('application/reactflow');
      if (!componentType) return;

      const position = screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      addNode(componentType, position);
    },
    [screenToFlowPosition, addNode]
  );

  /**
   * Handle React Flow initialization
   */
  const onInit = useCallback((instance: ReactFlowInstance) => {
    reactFlowInstance.current = instance;
  }, []);

  /**
   * Keyboard shortcuts
   */
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Check if target is an input element
      const target = event.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') return;

      const isCtrlOrCmd = event.ctrlKey || event.metaKey;

      // Delete selected
      if (event.key === 'Delete' || event.key === 'Backspace') {
        event.preventDefault();
        deleteSelected();
      }

      // Copy
      if (isCtrlOrCmd && event.key === 'c') {
        event.preventDefault();
        copySelected();
      }

      // Paste
      if (isCtrlOrCmd && event.key === 'v') {
        event.preventDefault();
        // Get center of viewport for paste position
        let position: { x: number; y: number } | undefined;
        if (reactFlowInstance.current) {
          const viewport = reactFlowInstance.current.getViewport();
          const { width, height } = reactFlowWrapper.current?.getBoundingClientRect() ?? {
            width: 800,
            height: 600,
          };
          // Calculate center of visible viewport in flow coordinates
          position = {
            x: (-viewport.x + width / 2) / viewport.zoom,
            y: (-viewport.y + height / 2) / viewport.zoom,
          };
        }
        paste(position);
      }

      // Undo
      if (isCtrlOrCmd && event.key === 'z' && !event.shiftKey) {
        event.preventDefault();
        undo();
      }

      // Redo
      if (isCtrlOrCmd && (event.key === 'y' || (event.key === 'z' && event.shiftKey))) {
        event.preventDefault();
        redo();
      }

      // Select all
      if (isCtrlOrCmd && event.key === 'a') {
        event.preventDefault();
        selectNodes(nodes.map((n) => n.id));
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => { window.removeEventListener('keydown', handleKeyDown); };
  }, [deleteSelected, copySelected, paste, undo, redo, selectNodes, nodes]);

  /**
   * Edge styling with selection state
   */
  const styledEdges = useMemo(() => {
    return edges.map((edge) => ({
      ...edge,
      style: {
        ...edge.style,
        stroke: getEdgeColor(edge.data?.medium),
        strokeWidth: selectedEdgeIds.includes(edge.id) ? 3 : 2,
      },
      animated: true,
    }));
  }, [edges, selectedEdgeIds]);

  /**
   * Node styling with selection state
   */
  const styledNodes = useMemo(() => {
    return nodes.map((node) => ({
      ...node,
      selected: selectedNodeIds.includes(node.id),
    }));
  }, [nodes, selectedNodeIds]);

  return (
    <div className="flex h-full bg-background" style={{ height }}>
      {/* Component Palette (desktop only) */}
      {!isMobile && (
        <div className="w-56 flex-shrink-0 border-r border-border">
          <ComponentPalette />
        </div>
      )}

      {/* Mobile: Palette drawer */}
      {isMobile && (
        <MobilePaletteDrawer
          isOpen={showPaletteDrawer}
          onClose={() => { setShowPaletteDrawer(false); }}
        />
      )}

      {/* React Flow Canvas */}
      <div className="flex-1 relative" ref={reactFlowWrapper}>
        <ReactFlow
          nodes={styledNodes}
          edges={styledEdges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onSelectionChange={onSelectionChange}
          onDragOver={onDragOver}
          onDrop={onDrop}
          onInit={onInit}
          isValidConnection={isValidConnection}
          nodeTypes={nodeTypes}
          fitView
          snapToGrid
          snapGrid={[15, 15]}
          deleteKeyCode={null} // We handle delete ourselves
          selectionKeyCode={null}
          multiSelectionKeyCode="Shift"
          panOnScroll
          selectionOnDrag
          panOnDrag={[1, 2]} // Pan with middle/right mouse button
        >
          <Background gap={15} size={1} />
          <Controls />
          <MiniMap
            nodeColor={getMiniMapNodeColor}
            nodeStrokeWidth={3}
            zoomable
            pannable
          />

          {/* Mobile: Components button */}
          {isMobile && (
            <Panel position="top-left">
              <button
                onClick={() => { setShowPaletteDrawer(true); }}
                className="flex items-center gap-2 rounded-lg bg-card px-3 py-2 text-sm font-medium shadow-md border border-border text-foreground hover:bg-muted"
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="7" height="7" />
                  <rect x="14" y="3" width="7" height="7" />
                  <rect x="14" y="14" width="7" height="7" />
                  <rect x="3" y="14" width="7" height="7" />
                </svg>
                <span>Components</span>
              </button>
            </Panel>
          )}

          {/* Toolbar Panel */}
          <Panel position="top-right" className="flex gap-1 md:gap-2">
            <button
              onClick={undo}
              disabled={!canUndo()}
              className="rounded bg-card px-2 md:px-3 py-1.5 text-sm shadow border border-border hover:bg-muted disabled:opacity-50 text-foreground"
              title="Undo (Ctrl+Z)"
            >
              {isMobile ? '↶' : 'Undo'}
            </button>
            <button
              onClick={redo}
              disabled={!canRedo()}
              className="rounded bg-card px-2 md:px-3 py-1.5 text-sm shadow border border-border hover:bg-muted disabled:opacity-50 text-foreground"
              title="Redo (Ctrl+Y)"
            >
              {isMobile ? '↷' : 'Redo'}
            </button>
            <button
              onClick={deleteSelected}
              disabled={selectedNodeIds.length === 0 && selectedEdgeIds.length === 0}
              className="rounded bg-card px-2 md:px-3 py-1.5 text-sm shadow border border-border hover:bg-muted disabled:opacity-50 text-foreground"
              title="Delete (Del)"
            >
              {isMobile ? '✕' : 'Delete'}
            </button>
          </Panel>

          {/* Status Panel */}
          <Panel position="bottom-left" className="rounded bg-card/90 px-3 py-1.5 text-sm shadow border border-border">
            <span className="text-muted-foreground">
              {nodes.length} components, {edges.length} connections
            </span>
            {isDirty && <span className="ml-2 text-amber-600 dark:text-amber-400">Modified</span>}
          </Panel>

          {/* Selection info */}
          {(selectedNodeIds.length > 0 || selectedEdgeIds.length > 0) && (
            <Panel position="bottom-right" className="rounded bg-card/90 px-3 py-1.5 text-sm shadow border border-border">
              <span className="text-muted-foreground">
                {selectedNodeIds.length > 0 && `${String(selectedNodeIds.length)} node${selectedNodeIds.length > 1 ? 's' : ''}`}
                {selectedNodeIds.length > 0 && selectedEdgeIds.length > 0 && ', '}
                {selectedEdgeIds.length > 0 && `${String(selectedEdgeIds.length)} edge${selectedEdgeIds.length > 1 ? 's' : ''}`}
                {' selected'}
              </span>
            </Panel>
          )}
        </ReactFlow>
      </div>
    </div>
  );
}

import { ReactFlowProvider } from 'reactflow';

/**
 * HVAC Editor with ReactFlowProvider wrapper
 */
export function HVACEditor(props: HVACEditorProps) {
  return (
    <ReactFlowProvider>
      <HVACEditorInner {...props} />
    </ReactFlowProvider>
  );
}
